import { useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AllFieldValueTypes,
  FieldDesc,
  Reference,
  EntityData,
  CodeReaderDesc,
  ComboDesc,
  CheckBoxListDesc,
  HierarchicalDesc,
} from '90.quickConnect.Models/models';
import { getReferencedValue } from '20.formLib/helpers/referencedValue/getReferencedValue';
import { FieldType } from '90.quickConnect.Models/enums';
import { useStore } from '30.quickConnect.Stores';
import { useQCUpdateFunctionsContext } from '20.formLib/DeclarationContainer/contexts';
import { buildCalculatedLabelWithSystemRefTokens } from '30.quickConnect.Stores/helpers/getValueForCalculateLabel';
import updateValueFieldOrChild from '20.formLib/helpers/updateField/updateValueFieldOrChild';
import getRefDependencies from '20.formLib/helpers/evalDependencies/getRefDependencies';

const useData = (
  fieldWithReference: FieldDesc,
  updateDeclaration: (updatedFieldFullPathId: string, newValue: AllFieldValueTypes) => void,
  forceFieldUpdate: (fieldPathIdToUpdate: string) => void,
  otherFields: FieldDesc[],
): void => {
  const { fullPathId, reference, value: referenceValue, id: fieldReferenceId } = fieldWithReference;
  const previousReferenceValue = useRef<string>();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sPatternSystemRefForStringExtract = new RegExp('SystemRef\\s*[(]\\s*(\\w+)\\s*[)]', 'g');
  const previousValue = useRef<AllFieldValueTypes>();

  const {
    QCScriptStore: { setFlattenFieldsByFullPathId },
    SharedListStore: { getSharedList, getSharedLists, sharedLists, logger },
    EquipmentsStore: { bindingSharedLists },
  } = useStore();

  const { updateProperty } = useQCUpdateFunctionsContext();

  // Récupération de la fonction de traduction
  const { t } = useTranslation('declaration');

  const {
    LoginStore: { signInInfos },
  } = useStore();

  const isMultiList = (ft: FieldType) => ft === FieldType.HierarchicalList || ft === FieldType.CheckBoxList;

  const referenceDependencies = useMemo(() => {
    if (reference) {
      const userInfoTokens = reference.match(sPatternSystemRefForStringExtract);
      if (userInfoTokens) {
        const labelValue = buildCalculatedLabelWithSystemRefTokens(reference, userInfoTokens, signInInfos);
        const shouldUpdateSystemRef = previousValue.current === undefined || previousValue.current !== labelValue;

        if (shouldUpdateSystemRef) {
          updateValueFieldOrChild(fieldWithReference.fullPathId, fieldWithReference, labelValue);
          // Sync QCScript store so getValAsText() ... reads the correct value during onFormLoaded
          setFlattenFieldsByFullPathId(fieldWithReference.fullPathId.toLowerCase(), labelValue);
          previousValue.current = labelValue;
        }
      }

      const indexOfColumn = reference.indexOf(':');

      // Sépare l'identifiant du champ de la colonne éventuelle (ex: "fieldId:columnId").
      const referenceIdOrFullPathId = indexOfColumn >= 0 ? reference.substring(0, indexOfColumn) : reference;

      const columnId = indexOfColumn >= 0 ? reference.substring(indexOfColumn + 1) : undefined;

      // Résout l'instance du champ correspondant au scope courant afin de gérer
      // correctement les champs dupliqués par les groupes répétables.
      const referencedField = getRefDependencies(referenceIdOrFullPathId, otherFields, fullPathId);

      if (!referencedField) {
        return [];
      }

      const { id: referencedId, value, fieldType, fullPathId: referenceFullPathId } = referencedField;

      const { listChoice } = referencedField as CheckBoxListDesc | ComboDesc | HierarchicalDesc;

      // Le champ référencé étant déjà résolu dans le bon scope,
      // une seule dépendance est nécessaire.
      return [
        {
          referenceId: referencedId,
          fullPathId: referenceFullPathId,
          value: isMultiList(fieldType) ? value ?? [] : value,
          fieldType,
          columnId,
          listChoice,
        } as Reference,
      ];
    }

    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference, sPatternSystemRefForStringExtract, otherFields, signInInfos, fieldWithReference, fullPathId]);

  // Récupère les listChoice des champs que le Reference Resolver en a besoin
  // Comme les champs liste dans un champ dialogue encore fermé
  useEffect(() => {
    const { listId, listChoice, fieldType } = fieldWithReference as CheckBoxListDesc | ComboDesc | HierarchicalDesc;
    if (listId && !listChoice) {
      getSharedList(listId).then(async (sharedList) => {
        try {
          if (!sharedList) {
            const shareldListsIds: string[] = [listId];
            await getSharedLists(shareldListsIds);
            sharedList = await getSharedList(listId);
            if (!sharedList) {
              return;
            }
          }
          const propertyToUpdate = fieldType === 'HierarchicalList' ? 'hierarchicalChoices' : 'data';
          const dataToUpdate = sharedList[propertyToUpdate];
          if (dataToUpdate) {
            updateProperty(fullPathId, 'listChoice', dataToUpdate);
          }
        } catch (error) {
          logger.trace(ReferenceError.name, error);
        }
      });
    }
  }, [
    referenceDependencies,
    otherFields,
    fullPathId,
    getSharedList,
    getSharedLists,
    updateProperty,
    logger,
    fieldWithReference,
  ]);

  const evalReference = useCallback(
    (referenceDependency: Reference) => {
      const { fieldType, fullPathId: referenceFullPathId } = referenceDependency;
      let entityReference: EntityData | undefined = undefined;

      const referenceField = otherFields.find(
        (field: FieldDesc) => field.fullPathId.toUpperCase() === referenceFullPathId.toUpperCase(),
      );
      if (!referenceField) return;

      if (fieldType === FieldType.CodeReader && referenceField) {
        const codeReaderField = referenceField as CodeReaderDesc;
        entityReference = codeReaderField.entityData;
      }

      // Charger les choix de liste si nécessaire sur l'entityReference de l'equipement
      if (entityReference) {
        const hasDicos = entityReference.valuesByLabel || entityReference.valuesById;
        const attributesAreDirectProperties = !entityReference?.attributes || entityReference.attributes.length === 0;

        // Fonction pour mapper la valeur d'un attribut en utilisant une liste partagée
        const mapAttributeValueUsingSharedList = (attributeKey: string, attributeValue: unknown): unknown => {
          // Trouver l'ID de la liste partagée pour la clé d'attribut
          let bindingId: string | undefined;
          for (const key in bindingSharedLists) {
            if (Object.prototype.hasOwnProperty.call(bindingSharedLists, key)) {
              if (key === attributeKey && bindingSharedLists[key] !== undefined) {
                const result = bindingSharedLists[key];
                bindingId = result.toString();
                break;
              }
            }
          }
          if (bindingId) {
            const sharedList = sharedLists?.find((list) => list.id === bindingId);
            if (sharedList) {
              // Trouver la valeur correspondante dans la liste partagée
              const choice = sharedList.data.find((c) => c.label === attributeValue || c.value === attributeValue);
              if (choice) {
                return choice;
              }
            }
          }
          return attributeValue;
        };
        // Mapper les valeurs des attributs en utilisant les listes partagées
        if (hasDicos) {
          // Mapping concis avec Object.fromEntries
          const mappedValuesByLabel = entityReference.valuesByLabel
            ? Object.fromEntries(
                Object.entries(entityReference.valuesByLabel).map(([key, value]) => [
                  key,
                  mapAttributeValueUsingSharedList(key, value),
                ]),
              )
            : undefined;

          const mappedValuesById = entityReference.valuesById
            ? Object.fromEntries(
                Object.entries(entityReference.valuesById).map(([key, value]) => [
                  key,
                  mapAttributeValueUsingSharedList(key, value),
                ]),
              )
            : undefined;

          entityReference = {
            ...entityReference,
            valuesByLabel: mappedValuesByLabel,
            valuesById: mappedValuesById,
          } as EntityData;
        } else if (attributesAreDirectProperties) {
          // Mapping robuste pour les propriétés directes
          const attributes = Object.entries(entityReference).reduce((acc, [key, value]) => {
            acc[key] = mapAttributeValueUsingSharedList(key, value);
            return acc;
          }, {} as { [key: string]: any });
          entityReference = { ...entityReference, attributes } as EntityData;
        } else {
          // Mapping robuste pour attributes[]
          const attributes = entityReference.attributes?.map((attr) => ({
            ...attr,
            value: mapAttributeValueUsingSharedList(attr.attributeLabel, attr.value),
          }));
          entityReference = { ...entityReference, attributes } as EntityData;
        }
      }

      const newValueForReferencedField = getReferencedValue(
        fieldWithReference,
        referenceField,
        referenceDependency,
        t,
        entityReference,
      );

      // Priorité aux références : on met à jour même si undefined, sauf si la valeur précédente était déjà la même
      // Cela permet aux références d'écraser les valeurs deeplink
      const shouldUpdate = previousValue.current === undefined || previousValue.current !== newValueForReferencedField;

      if (shouldUpdate) {
        updateDeclaration(fullPathId, newValueForReferencedField);
        previousValue.current = newValueForReferencedField;
      }
    },
    [otherFields, fieldWithReference, t, updateDeclaration, fullPathId, sharedLists, bindingSharedLists],
  );
  useEffect(() => {
    // Vérifie si les choix de liste pour le champ sont déjà chargés
    const areListChoicesLoaded = (field: FieldDesc) => {
      const { listId, listChoice } = field as CheckBoxListDesc | ComboDesc | HierarchicalDesc;
      return listId && (!listChoice || listChoice.length === 0);
    };

    // Vérifie si l'une des dépendances de référence nécessite le chargement des choix de liste
    const needListChoicesLoading =
      areListChoicesLoaded(fieldWithReference) ||
      referenceDependencies.some((refDep) => {
        const field = otherFields.find((f) => f.fullPathId.toUpperCase() === refDep.fullPathId.toUpperCase());
        return field && !!areListChoicesLoaded(field);
      });

    // Si les choix de liste doivent être chargés, retourne immédiatement
    if (needListChoicesLoading) return;

    // Dans le cas où la referenceValue a changé
    if (referenceDependencies.length > 0 && previousReferenceValue.current !== JSON.stringify(referenceDependencies)) {
      evalReference(referenceDependencies[0]);
      previousReferenceValue.current = JSON.stringify(referenceDependencies);
    }
  }, [
    evalReference,
    fieldReferenceId,
    fullPathId,
    referenceDependencies,
    reference,
    referenceValue,
    fieldWithReference,
    otherFields,
  ]);
};

export default useData;
